/**
 * useCabinAllLessons — course_cabin_main_v1 kursunun modülerini ve video
 * derslerini (lessons koleksiyonu) gruplu olarak döndürür.
 *
 * Strateji:
 *  1) GET /api/courses/course_cabin_main_v1/modules → modules (lessons koleksiyonu,
 *     module_id'ye göre gruplu, order'a göre sıralı)
 *  2) GET /api/enrollments → bu kursun completed_lessons[] → ilerleme işaretleri
 *
 * UYARI: /api/cabin/modules → cabin_lessons koleksiyonu (interaktif Duolingo dersleri,
 * vimeo_url=NULL). O endpoint bu hook'ta KULLANILMAZ.
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COURSE_ID = 'course_cabin_main_v1';

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchAllLessonsGrouped() {
  const headers = getAuthHeaders();

  // 1. Kurs modülleri + ders listesi (lessons koleksiyonu)
  const modulesRes = await axios.get(
    `${API}/courses/${COURSE_ID}/modules`,
    { headers }
  );

  // Response: { modules: [ { module_id, title, title_tr, title_en, order, num, lessons: [...] } ] }
  const rawModules = modulesRes.data?.modules ?? [];

  // 2. Enrollment: completed_lessons (opsiyonel — başarısız olursa ilerleme gösterilmez)
  let completedSet = new Set();
  try {
    const enrRes = await axios.get(`${API}/enrollments`, { headers });
    const myEnroll = (enrRes.data?.enrollments ?? []).find(
      (e) => e.course_id === COURSE_ID
    );
    if (myEnroll) {
      completedSet = new Set(myEnroll.completed_lessons || []);
    }
  } catch {
    // enrollment çekilemezse ilerleme olmadan devam et
  }

  // 3. Her modülü CabinVideoLessons accordion beklentisine map et
  return rawModules.map((mod) => {
    // Modülün derslerini globalOrder ile zenginleştir
    const lessons = (mod.lessons || []).map((lesson, idx) => ({
      lesson_id: lesson.lesson_id,
      title: lesson.title_tr || lesson.title || '',
      title_tr: lesson.title_tr || lesson.title || '',
      title_en: lesson.title_en || lesson.title || '',
      order: lesson.order ?? idx + 1,
      globalOrder: lesson.order ?? idx + 1,
      duration_minutes: lesson.duration_minutes ?? null,
      vimeo_url: lesson.vimeo_url || null,
      module_id: lesson.module_id || mod.module_id,
      // İlerleme: enrollment completed_lessons listesinden
      status: completedSet.has(lesson.lesson_id) ? 'completed' : 'available',
    }));

    return {
      module_id: mod.module_id,
      // slug yok — module_id kullan (CabinLessonPlayer artık kullanılmıyor)
      slug: mod.module_id,
      title: mod.title_tr || mod.title || mod.module_id,
      title_tr: mod.title_tr || mod.title || mod.module_id,
      title_en: mod.title_en || mod.title || mod.module_id,
      // Tasarım: renk ve emoji backend'de yok, sabit değerler
      gradient: 'from-sky-500 to-blue-600',
      emoji: '✈️',
      order: mod.order ?? 99,
      is_unlocked: true,
      progress: {
        percent:
          lessons.length > 0
            ? Math.round(
                (lessons.filter((l) => l.status === 'completed').length /
                  lessons.length) *
                  100
              )
            : 0,
      },
      lessons,
    };
  });
}

export function useCabinAllLessons() {
  return useQuery({
    queryKey: ['cabin', 'all-video-lessons-grouped', COURSE_ID],
    queryFn: fetchAllLessonsGrouped,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}
